import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LEARNING_HUB_MODULE_PATH, LEARNING_HUB_SEARCH_PATH } from '../definitions/learning-hub.constants';
import { LearningHubCategories, LearningHubLanguage } from '../definitions/learning-hub.models';
import { LearningHubService } from '../services/learning-hub.service';
import {
  categoryCommands,
  isCanonicalCategory,
  isLearningHubLanguage,
  resolveLegacyCategory
} from '../utils/learning-hub.utils';

/** Which kind of URL a route receives (route `data.legacy`). */
export type LearningHubLegacyKind = 'home' | 'category' | 'tag' | 'slug';

/**
 * Keeps the old WordPress URLs working once they are prefixed with /learning-hub (old path → same path under
 * the module), and the query params of the first version of this module:
 *
 *   /category/<nicename>[/<nicename>]/            → /learning-hub/category/<parent>[/<child>]?lang=<lang>
 *   /en|pt/category/<nicename>[/<nicename>]/      → idem, with the language of the prefix
 *   /tag/<tag>/, /en|pt/tag/<tag>/                → /learning-hub/search?lang=<lang>&tag=<tag>
 *   /en/, /pt/ (old homes)                        → /learning-hub?lang=<lang>
 *   /learning-hub?category=..|tag=..|q=..         → category page / search page
 *
 * Canonical URLs pass through; unknown categories pass through too so the page shows its "not found" state.
 * Provided by LearningHubModule (it needs the module's LearningHubService).
 */
@Injectable()
export class LearningHubLegacyRedirectGuard implements CanActivate {
  constructor(private readonly _router: Router, private readonly _learningHubService: LearningHubService) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean | UrlTree {
    const kind: LearningHubLegacyKind = route.data?.legacy;
    const params = route.paramMap;
    const query = route.queryParamMap;
    const langParam = params.get('lang');
    if (langParam !== null && !isLearningHubLanguage(langParam)) {
      return this._home(null);
    }
    const prefixLang = langParam as LearningHubLanguage | null;
    const queryLang = isLearningHubLanguage(query.get('lang')) ? (query.get('lang') as LearningHubLanguage) : null;

    switch (kind) {
      case 'tag':
        return this._search(prefixLang || queryLang || 'es', { tag: params.get('tag') });
      case 'slug':
        return isLearningHubLanguage(params.get('slug')) ? this._home(params.get('slug') as LearningHubLanguage) : true;
      case 'home':
        if (query.get('category')) {
          return this._category([query.get('category')], queryLang, false);
        }
        if (query.get('tag') || query.get('q')) {
          return this._search(queryLang, { tag: query.get('tag'), q: query.get('q') });
        }
        return true;
      case 'category':
        return this._category(
          [params.get('parent'), params.get('child')].filter(Boolean),
          prefixLang || queryLang,
          prefixLang === null
        );
      default:
        return true;
    }
  }

  private _category(
    segments: string[],
    lang: LearningHubLanguage | null,
    allowCanonical: boolean
  ): Observable<boolean | UrlTree> {
    return this._learningHubService.getCategories().pipe(
      map(({ categories }: LearningHubCategories) => {
        if (allowCanonical && isCanonicalCategory(segments, categories)) {
          return true;
        }
        const target = resolveLegacyCategory(segments, categories, lang);
        if (!target) {
          // Unknown category: show the "not found" state of the category page.
          return allowCanonical
            ? true
            : this._router.createUrlTree(['/', LEARNING_HUB_MODULE_PATH, 'category', ...segments], this._lang(lang));
        }
        return this._router.createUrlTree(categoryCommands(target.key, categories), this._lang(target.lang || lang));
      }),
      // Without the tree we cannot translate the URL; let the page handle it (it shows its error state).
      catchError(() => of(true))
    );
  }

  private _search(lang: LearningHubLanguage | null, params: Record<string, string | null>): UrlTree {
    const queryParams = Object.entries({ lang, ...params }).reduce(
      (acc: Record<string, string>, [key, value]: [string, string | null]) => (value ? { ...acc, [key]: value } : acc),
      {}
    );
    return this._router.createUrlTree(['/', LEARNING_HUB_MODULE_PATH, LEARNING_HUB_SEARCH_PATH], { queryParams });
  }

  private _home(lang: LearningHubLanguage | null): UrlTree {
    return this._router.createUrlTree(['/', LEARNING_HUB_MODULE_PATH], this._lang(lang));
  }

  private _lang(lang: LearningHubLanguage | null): { queryParams?: Record<string, string> } {
    return lang ? { queryParams: { lang } } : {};
  }
}
